import React, { Fragment, ReactElement, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isValid from 'date-fns/isValid';
import { Box, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  createAudioMessage,
  createMessage,
  markMessagesAsRead
} from 'state/modules/entity/custom/assignment-attempt-mutations/thunks';
import { getStudent } from 'state/modules/entity/selectors/student-selectors';
import { AppStoreState, Message } from 'types';
import { remCalc } from 'util/style-util';
import { MessageBubble } from './MessageBubble';
import { ScrollToBottom } from './ScrollToBottom';
import { MessageInput } from './MessageInput';
import { translate } from '../../../../translations/translator';
import { createEvent } from 'api';
import { MESSAGE_SENDER as MessageSender } from 'types/enums';
import { formatUiDate } from 'util/date-util';

const t = key => translate('assignmentTracker', key);

interface Props {
  assignmentId: number;
  studentId: number;
  messages: Message[];
}

// TODO: to avoid page slowdown render only latest X elements, and show a button to load more at top ?
export const Messenger = ({ assignmentId, studentId, messages = [] }: Props): JSX.Element => {
  const { daySeparator, dayStyling, mainBox } = useStyles();
  const dispatch = useDispatch() as any;
  const user = useSelector(({ user }: AppStoreState) => user);
  const student = useSelector(state => getStudent(state, { studentId })) ?? null;

  const [playingAudioMessage, setPlayingAudioMessage] = useState<HTMLAudioElement>();

  const attemptId = messages[0]?.attemptId;
  const studentName = `${student?.firstName} ${student?.lastName}`;

  useEffect(() => {
    return () => {
      const newMessages = messages.filter(
        ({ sender, dateOpened }) => sender === MessageSender.STUDENT && !dateOpened
      );

      if (newMessages.length > 0) {
        const messageIds = newMessages.map(message => message.id);
        dispatch(markMessagesAsRead(assignmentId, studentId, messageIds));
      }
    };
  }, [dispatch, assignmentId, studentId, messages]);

  const handleSend = (text: string) => {
    dispatch(createMessage(assignmentId, studentId, { attemptId, text }));
    createEvent('messageSent', {
      assignmentId,
      studentId,
      attemptId,
      messageType: 'text'
    });
  };

  const handleSendAudio = (audioFile: File) => {
    dispatch(createAudioMessage(assignmentId, studentId, { attemptId, file: audioFile }));
    createEvent('messageSent', {
      assignmentId,
      studentId,
      attemptId,
      messageType: 'audio'
    });
  };

  const onStartPlaying = (newAudioMessagePlaying: HTMLAudioElement) => {
    if (
      playingAudioMessage &&
      !playingAudioMessage.paused &&
      playingAudioMessage !== newAudioMessagePlaying
    ) {
      playingAudioMessage.pause();
    }
    setPlayingAudioMessage(newAudioMessagePlaying);
  };

  const getFormattedDate = (dateStr: string | undefined): string => {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    return isValid(date) ? formatUiDate(date) : '';
  };
  const dateSet = new Set<string>();

  const getLine = (message: Message): JSX.Element => (
    <div className={daySeparator}>
      <span className={dayStyling}>{getFormattedDate(message.dateSent)}</span>
    </div>
  );

  const getMessageBubble = (message: Message): JSX.Element => (
    <MessageBubble
      message={message}
      userId={user?.me?.id}
      studentName={studentName}
      onStartPlaying={onStartPlaying}
    />
  );

  return (
    <Box mt={3} className={mainBox}>
      <Box>
        <Box height={400}>
          <ScrollToBottom noContentMessage={t('noMessages')}>
            {messages.map(
              (message: Message, index: number): ReactElement => {
                const content = !dateSet.has(getFormattedDate(message.dateSent)) ? (
                  <Fragment key={index}>
                    {getLine(message)}
                    {getMessageBubble(message)}
                  </Fragment>
                ) : (
                  <Fragment key={index}>{getMessageBubble(message)}</Fragment>
                );
                dateSet.add(getFormattedDate(message.dateSent));
                return content;
              }
            )}
          </ScrollToBottom>
        </Box>
        <Box px={3}>
          <MessageInput onSend={handleSend} onSendAudio={handleSendAudio} />
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    mainBox: {
      borderRadius: '4px'
    },
    daySeparator: () => ({
      position: 'relative',
      width: '100%',
      height: remCalc(1),
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.secondary.main,
      margin: `${remCalc(32)} 0`
    }),
    dayStyling: () => ({
      position: 'absolute',
      // center horizontally and vertically absolute positioned element - START
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      // center horizontally and vertically absolute positioned element - END
      zIndex: 1,
      fontSize: remCalc(12),
      lineHeight: 'normal',
      backgroundColor: '#fff',
      padding: `0 ${remCalc(5)}`
    })
  };
});
